import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../icons.jsx';
import { LizziLogo } from '../components.jsx';
import { Field } from './login.jsx';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from '../auth/AuthContext.jsx';
import { validatePassword, isPasswordPwned, isRecentRecoverySession, MIN_PASSWORD_LENGTH } from '../auth/passwordPolicy.js';

const { useState, useEffect } = React;

// ==========================================
// Restablecer contraseña — ruta pública (enlace de recuperación)
//
// El enlace del correo puede llegar de varias formas:
//  - #access_token=…&type=recovery (flujo implícito, lo procesa supabase-js)
//  - ?code=… (flujo PKCE, lo procesa supabase-js)
//  - ?token_hash=…&type=recovery (plantilla de correo con TokenHash; lo
//    verificamos aquí, así los escáneres de correo no consumen el enlace)
//  - #error=…&error_code=otp_expired (enlace caducado o ya usado)
//
// Seguridad:
//  - Solo se acepta una sesión de recuperación reciente (amr "recovery"/"otp"
//    en el JWT o evento PASSWORD_RECOVERY), nunca una sesión normal: así nadie
//    con un portátil desbloqueado cambia la contraseña sin el correo.
//  - Si la cuenta tiene MFA se exige el código TOTP (aal2) antes del cambio.
//  - Política de contraseñas + comprobación de filtraciones (HIBP, k-anonimato).
//  - Tras el cambio se cierran TODAS las sesiones abiertas de la cuenta.
// ==========================================
let recoveryVerification = null; // evita verificar dos veces el token (StrictMode)

function readRecoveryParams() {
  const query = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const get = (k) => query.get(k) || hash.get(k);
  return {
    tokenHash: get('token_hash'),
    type: get('type'),
    errorCode: get('error_code'),
    errorDescription: get('error_description'),
    error: get('error'),
  };
}

function clearUrlSecrets() {
  if (window.location.search || window.location.hash) {
    window.history.replaceState(null, '', window.location.pathname);
  }
}

function friendlyAuthError(err) {
  const msg = (err?.message || '').toLowerCase();
  const code = err?.code || '';
  if (code === 'same_password' || msg.includes('different from the old')) return 'La nueva contraseña debe ser distinta de la anterior.';
  if (code === 'weak_password' || msg.includes('weak') || msg.includes('pwned') || msg.includes('leaked')) return 'La contraseña es demasiado débil o aparece en filtraciones conocidas. Elige otra.';
  if (code === 'insufficient_aal' || msg.includes('aal2')) return 'Tu cuenta tiene verificación en dos pasos: introduce el código de tu app de autenticación.';
  if (code === 'over_request_rate_limit' || msg.includes('rate limit')) return 'Demasiados intentos. Espera unos minutos y vuelve a probar.';
  if (code === 'session_not_found' || code === 'session_expired' || code === 'reauthentication_needed' || msg.includes('session')) return 'El enlace ha caducado. Solicita uno nuevo desde la pantalla de inicio de sesión.';
  return 'No se ha podido actualizar la contraseña. Inténtalo de nuevo o solicita un enlace nuevo.';
}

function ResetPasswordScreen() {
  const navigate = useNavigate();
  const { passwordRecovery, clearPasswordRecovery } = useAuth();
  const [status, setStatus] = useState('checking'); // checking | ready | invalid
  const [invalidReason, setInvalidReason] = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const params = readRecoveryParams();

    const markReady = async (session) => {
      if (cancelled) return;
      setEmail(session?.user?.email || '');
      try {
        const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (!cancelled) setNeedsMfa(data?.nextLevel === 'aal2' && data?.currentLevel !== 'aal2');
      } catch (e) {}
      clearUrlSecrets();
      if (!cancelled) setStatus('ready');
    };

    const markInvalid = (reason) => {
      clearUrlSecrets();
      if (!cancelled) { setInvalidReason(reason); setStatus('invalid'); }
    };

    const isRecovery = (session, event) =>
      !!session && (event === 'PASSWORD_RECOVERY' || passwordRecovery || isRecentRecoverySession(session));

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (isRecovery(session, event)) markReady(session);
    });

    (async () => {
      if (params.error || params.errorCode) {
        const expired = params.errorCode === 'otp_expired' || /expired|invalid/i.test(params.errorDescription || '');
        markInvalid(expired
          ? 'El enlace ha caducado o ya se ha utilizado. Los enlaces de recuperación solo sirven una vez y durante un tiempo limitado.'
          : 'El enlace de recuperación no es válido.');
        return;
      }

      if (params.tokenHash) {
        if (params.type && params.type !== 'recovery') { markInvalid('El enlace de recuperación no es válido.'); return; }
        if (!recoveryVerification) {
          recoveryVerification = supabase.auth.verifyOtp({ token_hash: params.tokenHash, type: 'recovery' });
        }
        const { data, error: vErr } = await recoveryVerification;
        if (vErr || !data?.session) { markInvalid('El enlace ha caducado o ya se ha utilizado. Solicita uno nuevo.'); return; }
        markReady(data.session);
        return;
      }

      // Flujos implícito / PKCE: supabase-js procesa la URL al inicializarse.
      const check = async (final) => {
        if (cancelled) return;
        const { data: { session } } = await supabase.auth.getSession();
        if (isRecovery(session)) { markReady(session); return; }
        if (!final) { timer = setTimeout(() => check(true), 1500); return; }
        markInvalid(session
          ? 'Esta página solo funciona desde el enlace de recuperación que te enviamos por correo. Para cambiar tu contraseña con la sesión iniciada, cierra sesión y usa «¿Has olvidado tu contraseña?».'
          : 'No hemos podido validar el enlace. Puede que haya caducado o que ya se haya utilizado.');
      };
      check(false);
    })();

    return () => { cancelled = true; clearTimeout(timer); sub.subscription.unsubscribe(); };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    const policyError = validatePassword(password, { email });
    if (policyError) { setError(policyError); return; }
    if (password !== confirm) { setError('Las contraseñas no coinciden.'); return; }
    if (needsMfa && code.length !== 6) { setError('Introduce el código de 6 dígitos de tu app de autenticación.'); return; }
    setLoading(true);

    if (await isPasswordPwned(password)) {
      setLoading(false);
      setError('Esta contraseña aparece en filtraciones de datos públicas. Elige otra distinta.');
      return;
    }

    if (needsMfa) {
      const { data: factorsData, error: listErr } = await supabase.auth.mfa.listFactors();
      const totp = (factorsData?.totp || []).find((f) => f.status === 'verified');
      if (listErr || !totp) { setLoading(false); setError('No se ha podido verificar el segundo factor. Contacta con un administrador.'); return; }
      const { error: vErr } = await supabase.auth.mfa.challengeAndVerify({ factorId: totp.id, code });
      if (vErr) { setLoading(false); setCode(''); setError('Código de verificación incorrecto. Inténtalo de nuevo.'); return; }
      setNeedsMfa(false);
    }

    const { error: err } = await supabase.auth.updateUser({
      password,
      data: { must_change_password: false },
    });
    if (err) { setLoading(false); setError(friendlyAuthError(err)); return; }

    // Cierra todas las sesiones de la cuenta (incluida esta): quien tuviera
    // la contraseña antigua o una sesión robada queda fuera.
    try { await supabase.auth.signOut({ scope: 'global' }); } catch (e) {
      try { await supabase.auth.signOut({ scope: 'local' }); } catch (e2) {}
    }
    clearPasswordRecovery();
    setPassword(''); setConfirm(''); setCode('');
    setLoading(false);
    setDone(true);
  };

  if (status === 'checking') {
    return (
      <AuthShell title="Nueva contraseña" subtitle="Validando el enlace de recuperación…">
        <div style={{display:'flex', justifyContent:'center', padding:'12px 0'}}>
          <span className="skeleton" style={{width:28, height:28, borderRadius:999}}></span>
        </div>
      </AuthShell>
    );
  }

  if (status === 'invalid') {
    return (
      <AuthShell title="Enlace no válido" subtitle={invalidReason}>
        <div style={{display:'flex', flexDirection:'column', gap:12}}>
          <p style={{fontSize:13.5, color:'var(--text-3)', lineHeight:1.5, margin:0}}>
            Vuelve a la pantalla de inicio de sesión, pulsa «¿Has olvidado tu contraseña?» y abre el enlace del correo más reciente.
          </p>
          <button type="button" className="btn btn-primary" style={{padding:'14px 18px', justifyContent:'center', borderRadius:12, marginTop:4}}
            onClick={async () => { try { await supabase.auth.signOut({ scope: 'local' }); } catch (e) {} navigate('/login', { replace: true }); }}>
            Solicitar un nuevo enlace
          </button>
        </div>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Contraseña actualizada" subtitle="Por seguridad hemos cerrado todas las sesiones abiertas de tu cuenta.">
        <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:14, padding:'12px 0'}}>
          <div style={{width:48, height:48, borderRadius:999, background:'var(--sem-success-bg, var(--bg-2))', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--sem-success)'}}>
            <Icon.Check size={24}/>
          </div>
          <button type="button" className="btn btn-primary" style={{padding:'14px 18px', justifyContent:'center', borderRadius:12, width:'100%'}} onClick={() => navigate('/login', { replace: true })}>
            Iniciar sesión con la nueva contraseña
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Nueva contraseña" subtitle={`Define una contraseña segura${email ? ` para ${email}` : ''}. Mínimo ${MIN_PASSWORD_LENGTH} caracteres combinando mayúsculas, minúsculas, números o símbolos.`}>
      <form onSubmit={submit} style={{display:'flex', flexDirection:'column', gap:14}} autoComplete="on">
        <input type="email" name="username" autoComplete="username" value={email} readOnly hidden />
        <Field label="Nueva contraseña" type="password" value={password} onChange={setPassword} placeholder={`Mínimo ${MIN_PASSWORD_LENGTH} caracteres`} icon="Shield"/>
        <Field label="Repite la contraseña" type="password" value={confirm} onChange={setConfirm} placeholder="Vuelve a escribirla" icon="Shield"/>
        {needsMfa && (
          <Field label="Código de verificación (MFA)" type="text" value={code} onChange={(v) => setCode(v.replace(/\D/g, '').slice(0, 6))} placeholder="000000" icon="Shield"/>
        )}
        {error && <div role="alert" style={{fontSize:12.5, color:'var(--sem-danger)'}}>{error}</div>}
        <button type="submit" className="btn btn-primary" style={{padding:'14px 18px', justifyContent:'center', borderRadius:12, marginTop:4}} disabled={loading}>
          {loading ? 'Guardando…' : 'Guardar contraseña'}
        </button>
      </form>
    </AuthShell>
  );
}

// ==========================================
// Verificación MFA — challenge de un factor TOTP
// ==========================================
function MfaChallengeScreen() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const verify = async (e) => {
    e.preventDefault();
    setError('');
    if (code.length !== 6) { setError('Introduce el código de 6 dígitos.'); return; }
    setLoading(true);
    const { data: factorsData, error: listErr } = await supabase.auth.mfa.listFactors();
    if (listErr) { setLoading(false); setError(listErr.message); return; }
    const totp = (factorsData?.totp || []).find((f) => f.status === 'verified');
    if (!totp) { setLoading(false); setError('No hay ningún factor MFA verificado en esta cuenta.'); return; }
    const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({ factorId: totp.id });
    if (chErr) { setLoading(false); setError(chErr.message); return; }
    const { error: vErr } = await supabase.auth.mfa.verify({ factorId: totp.id, challengeId: challenge.id, code });
    setLoading(false);
    if (vErr) { setError('Código incorrecto. Inténtalo de nuevo.'); return; }
    navigate('/');
  };

  return (
    <AuthShell title="Verificación en dos pasos" subtitle="Introduce el código de tu app de autenticación (Google Authenticator, 1Password…).">
      <form onSubmit={verify} style={{display:'flex', flexDirection:'column', gap:14}}>
        <input
          inputMode="numeric"
          autoFocus
          maxLength={6}
          value={code}
          onChange={(e)=>setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          style={{
            textAlign:'center', letterSpacing:'0.5em', fontSize:28, fontFamily:'JetBrains Mono',
            padding:'16px', borderRadius:12, border:'1px solid var(--line-2)',
            background:'var(--bg-1)', color:'var(--text-1)',
          }}
        />
        {error && <div style={{fontSize:12.5, color:'var(--sem-danger)', textAlign:'center'}}>{error}</div>}
        <button type="submit" className="btn btn-primary" style={{padding:'14px 18px', justifyContent:'center', borderRadius:12}} disabled={loading}>
          {loading ? 'Verificando…' : 'Verificar y entrar'}
        </button>
        <button type="button" className="btn btn-ghost" style={{justifyContent:'center', fontSize:12.5}} onClick={async ()=>{ await supabase.auth.signOut(); navigate('/login'); }}>
          Cancelar y cerrar sesión
        </button>
      </form>
    </AuthShell>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div style={{minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg-0)', padding:24, position:'relative', overflow:'hidden'}}>
      <div className="glow-orb" style={{ width:480, height:480, background:'var(--primary)', top:-160, right:-120, opacity:0.18 }}></div>
      <div style={{width:'100%', maxWidth:420, position:'relative', zIndex:2}}>
        <div style={{marginBottom:28}}><LizziLogo size={30}/></div>
        <h2 className="font-display-heavy" style={{fontSize:36, margin:'0 0 10px 0', letterSpacing:'-0.035em', lineHeight:1}}>{title}</h2>
        <p style={{color:'var(--text-3)', fontSize:14, marginBottom:28, lineHeight:1.5}}>{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

// ==========================================
// Cambio de contraseña obligatorio en el primer inicio de sesión
// El admin crea la cuenta con una contraseña temporal; aquí el usuario
// fija su propia contraseña y se limpia el flag must_change_password.
// ==========================================
function ChangePasswordScreen() {
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const policyError = validatePassword(password, { email: user?.email });
    if (policyError) { setError(policyError); return; }
    if (password !== confirm) { setError('Las contraseñas no coinciden.'); return; }
    setLoading(true);
    if (await isPasswordPwned(password)) {
      setLoading(false);
      setError('Esta contraseña aparece en filtraciones de datos públicas. Elige otra distinta.');
      return;
    }
    const { error: err } = await supabase.auth.updateUser({
      password,
      data: { must_change_password: false },
    });
    setLoading(false);
    if (err) { setError(friendlyAuthError(err)); return; }
    setDone(true);
    setTimeout(() => navigate('/'), 1200);
  };

  return (
    <AuthShell
      title="Crea tu contraseña"
      subtitle="Tu cuenta se creó con una contraseña temporal. Define una contraseña propia para continuar."
    >
      {done ? (
        <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:14, padding:'12px 0'}}>
          <div style={{width:48, height:48, borderRadius:999, background:'var(--sem-success-bg, var(--bg-2))', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--sem-success)'}}>
            <Icon.Check size={24}/>
          </div>
          <p style={{fontSize:14, color:'var(--text-2)', textAlign:'center'}}>Contraseña creada. Entrando…</p>
        </div>
      ) : (
        <form onSubmit={submit} style={{display:'flex', flexDirection:'column', gap:14}}>
          <Field label="Nueva contraseña" type="password" value={password} onChange={setPassword} placeholder={`Mínimo ${MIN_PASSWORD_LENGTH} caracteres`} icon="Shield"/>
          <Field label="Repite la contraseña" type="password" value={confirm} onChange={setConfirm} placeholder="Vuelve a escribirla" icon="Shield"/>
          {error && <div style={{fontSize:12.5, color:'var(--sem-danger)'}}>{error}</div>}
          <button type="submit" className="btn btn-primary" style={{padding:'14px 18px', justifyContent:'center', borderRadius:12, marginTop:4}} disabled={loading}>
            {loading ? 'Guardando…' : 'Guardar y entrar'}
          </button>
          <button type="button" className="btn btn-ghost" style={{justifyContent:'center', fontSize:12.5}} onClick={async ()=>{ await signOut(); navigate('/login'); }}>
            Cancelar y cerrar sesión
          </button>
        </form>
      )}
    </AuthShell>
  );
}

export { ResetPasswordScreen, MfaChallengeScreen, ChangePasswordScreen };
